import { AttributePlainEnumValue, Image, Money } from '@commercetools/platform-sdk';
import {
  INutsProductAttributes as NutsProductAttributes,
  INutsVariantAttributes as NutsVariantAttributes,
  pivotAttributeValues,
} from '@nuts/auto-delivery-sdk/dist/utils/helpers';
import type { IsEqual } from 'type-fest';

import { PriceFragment } from '@/graphql/fragments/price';
import { findListingImage } from '@/utils/cart';
import { ImageBySize } from '@/utils/image';
import proxiedImageUtil from '@/utils/imageProxied';
import { RawAttributeValues } from '@/utils/product';

/**
 * Common attributes for Product Projection Search variants
 *
 * @todo: Consider `ProductCard.fromProductProjectionVariant()` support.
 */
interface ProductProjectionVariantAttributes
  extends Pick<
      NutsProductAttributes,
      | 'customProduct'
      | 'merchandisingCategory'
      | 'orderHint'
      | 'reportingCategory'
      | 'requiresCustomization'
    >,
    Pick<
      NutsVariantAttributes,
      | 'active'
      | 'autoDeliveryEligible'
      | 'backordered'
      | 'bulk'
      | 'variantName'
      | 'weight'
      | 'wholesale'
    > {
  pieceCost?: Money;
}

/**
 * Raw attribute data to pass through {@link pivotAttributeValues}
 *
 * See `@nuts/auto-delivery-sdk/dist/utils/helpers`
 */
export type ProductProjectionVariantAttributesRaw = RawAttributeValues<
  Omit<ProductProjectionVariantAttributes, 'backordered'> & {
    backordered: AttributePlainEnumValue;
    pieceCost?: Money;
  }
>;

export interface ProductProjectionVariant {
  attributesRaw: ProductProjectionVariantAttributesRaw;
  images?: Pick<Image, 'label' | 'url'>[];
  prices: PriceFragment[];
  sku: string;
}

export interface WithAllVariants {
  allVariants: ProductProjectionVariant[];
}

export interface WithMasterVariant {
  masterVariant: ProductProjectionVariant;
}

/**
 * Common attributes for Product Projection Search results.
 *
 * To be used when building product lists, for a consistent shape across the application. Modeled
 * after the `ProductCardData` concept.
 *
 * To distinguish between `WithAllVariants` and `WithMasterVariant`, must be combined with the TS
 * helper `Extract<T>`. E.g. `Extract<ProductProjectionResult, WithAllVariants>`.
 *
 * Current use cases do not need to construct a product path. When this becomes necessary, we can
 * abstract the behavior in `@/api/productPathsById` and `@/graphql/productPathsById`.
 */
export type ProductProjectionResult = (WithAllVariants | WithMasterVariant) & {
  key: string;
  name: string;
};

export interface NutsProductProjection
  extends Pick<ProductProjectionResult, 'name'>,
    Omit<ProductProjectionVariant, 'attributesRaw' | 'images'>,
    ProductProjectionVariantAttributes {
  listImage: ImageBySize;
  listPrice: PriceFragment;
  productKey: ProductProjectionResult['key'];
}

export const NutsProductProjection = {
  fromQueryResult(result: ProductProjectionResult): NutsProductProjection {
    const { attributesRaw, images, ...masterVariant } =
      'allVariants' in result ? result.allVariants[0] : result.masterVariant;

    const attributes = pivotAttributeValues(attributesRaw);
    const image = findListingImage(images ?? []);
    const listPrice = masterVariant.prices.find((p) => !p.channel);

    if (!image || !listPrice) {
      const missingKeys = Object.entries({ listImage: image, listPrice })
        .filter(([_, value]) => !value)
        .map(([key]) => key)
        .join(', ');
      const message = `Failed to build NutsProductProjection for ${result.key}; missing ${missingKeys}`;
      throw new Error(message);
    }

    const [listImage] = proxiedImageUtil.getVariants([image]);
    return {
      ...attributes.product,
      ...attributes.variant,
      ...masterVariant,
      listImage,
      listPrice,
      name: result.name,
      pieceCost: attributesRaw.find((a) => a.name === 'pieceCost')?.value, // not pivoted
      productKey: result.key,
    };
  },
};

export const attributeNames = [
  'active',
  'autoDeliveryEligible',
  'backordered',
  'bulk',
  'customProduct',
  'merchandisingCategory',
  'orderHint',
  'pieceCost',
  'reportingCategory',
  'requiresCustomization',
  'variantName',
  'weight',
  'wholesale',
] as const;

type AttributeNames = (typeof attributeNames)[number];
type KeysMatch = IsEqual<AttributeNames, keyof ProductProjectionVariantAttributes>;

/**
 * Force the constraint to be checked at compile time with a direct value assignment.
 * If you comment out any key above or add an extra one, this line will trigger a
 * type error: "Type 'true' does not satisfy the constraint 'never'"
 */
export const ENSURE_KEYS_MATCH: KeysMatch extends true ? true : never = true;
